import { Form, FormItem, useForm, useFormContext } from '@magicdoor/form';
import { useLocalization } from '@magicdoor/ui';
import { createMemo, onMount } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { Input } from '~/components/auth/InputField';
import { SignInTitle } from '~/components/auth/sign-in/SignInTitle';
import { Button } from '~/components/common/Buttons';
import { LabeledSelect } from '~/components/common/Inputs/LabeledSelect';
import { OtpLoginProvider, OtpLoginStep, useMagicLinkLogin, useOtpLogin } from '~/contexts/local';
import type { Component } from 'solid-js';
import type { SendOtpDto } from '~/swagger/Api';

const OtpSignIn = () => {
  const { t } = useLocalization();
  const { loginStep, sendOtp } = useOtpLogin();
  const form = useForm();

  const stepMap: Record<OtpLoginStep, Component> = {
    [OtpLoginStep.InputPhone]: OtpInputPhone,
    [OtpLoginStep.SelectCompany]: OtpSelectCompany,
    [OtpLoginStep.InputVerifyCode]: OtpInputVerifyCode,
  };

  const handleSubmit = async () => {
    const { validateStatus } = form.validateForm();
    if (!validateStatus) return;
    await sendOtp(form.formStore as SendOtpDto);
  };

  return (
    <Form defaultForm={form}>
      <SignInTitle title={t('Sign in')} subTitle="With phone number" />
      <div class="flex flex-col gap-4">
        <Dynamic component={stepMap[loginStep()]} />
        <Button type="button" loading={sendOtp.loading} onClick={handleSubmit} rounded="full" class="flex w-full justify-center py-3.5">
          {t('Get verification code')}
        </Button>
      </div>
    </Form>
  );
};

const OtpInputPhone = () => {
  const { t } = useLocalization();

  return (
    <Input
      label={t('Phone')}
      placeholder={t('Enter your phone number')}
      formFieldName="phone"
      rules={[
        { required: true, message: t('{name} is required', { name: t('Phone') }) },
        { type: 'phone', message: t('{name} is invalid', { name: t('Phone') }) },
      ]}
    />
  );
};

const OtpSelectCompany = () => {
  const form = useFormContext();
  const { companies } = useMagicLinkLogin();
  const options = createMemo(() => companies().map((company) => ({ label: company.name, value: company.id })));
  onMount(() => {
    if (companies().length) {
      form.setFieldValue('companyId', companies()[0].id);
    }
  });
  return <FormItem formFieldName="companyId" component={LabeledSelect} selectClass="h-12 rounded-full" options={options()} />;
};

const OtpInputVerifyCode = () => {
  const { t } = useLocalization();

  return <Input label={t('Verify code')} placeholder={t('Enter your verify code')} formFieldName="otp" />;
};

export const OtpSignInView = () => {
  return (
    <OtpLoginProvider>
      <OtpSignIn />
    </OtpLoginProvider>
  );
};
